import { autorun, makeAutoObservable, runInAction } from "mobx";
import {
  CleanupFunction,
  DbotEventDispatcher,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  isLanguageChanged,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  ProjectDocumentStatus,
} from "@/dbot/common/commonDomain";
import { Language } from "@/i18n/languages";
import { DocumentLanguage, LocalizedText } from "@/common/common";
import { DocumentType } from "@/common/documents";
import {
  downloadSignedSubscriptionFormsAction,
  openSendForSigningDialogAction,
  openSubscriptionFormBatchPreviewDialogAction,
} from "@/dbot/summary/summaryDomain";
import { DocumentSigningStatus } from "@/document/documentApi";
import { DecisionType } from "@/common/decisions";
import { TMessage } from "@/i18n/tt";
import { FileToPreview } from "@/components-v2/BatchPreviewDialog/batchPreviewDomain";
import {
  documentLanguageLabels,
  RadioFieldModel,
  requiredOfficialDocumentLanguageFieldModel,
} from "@/common/commonForms";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";

export type SubscriptionForm = {
  type: DocumentType.subscription_form;
  documentLanguage: Language[];
  additionalDetailsTexts?: LocalizedText[];
};

export class SubscriptionFormForm {
  readonly type = DocumentType.subscription_form;
  readonly dispatch: DbotEventDispatcher;
  context: DocumentContext<SubscriptionForm>;
  documentLanguage: RadioFieldModel<DocumentLanguage>;
  additionalDetailsTextModel: LocalizedTextEditorModel;
  additionalDetailsTexts: LocalizedText[];

  constructor(dispatch: DbotEventDispatcher, context: DocumentContext<SubscriptionForm>) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.documentLanguage = requiredOfficialDocumentLanguageFieldModel({
      value: mapLocalesToDocumentLanguage(context.documentData.documentLanguage),
    });
    this.additionalDetailsTexts = context.documentData.additionalDetailsTexts ?? [];
    this.additionalDetailsTextModel = new LocalizedTextEditorModel({
      texts: context.documentData.additionalDetailsTexts,
      isReadOnly: () => context.isLocked,
      label: { key: "documents.subscriptionForm.additionalDetails" },
      aiClient: context.aiClient,
    });
  }

  init(): CleanupFunction {
    return autorun(() => this.handleLanguageChanges());
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (isLanguageChanged(documentLanguages, this.additionalDetailsTextModel.textModels)) {
      runInAction(() => {
        this.additionalDetailsTextModel.onLanguageChange(
          documentLanguages,
          this.additionalDetailsTexts,
        );
      });
    }
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  get documentId() {
    return this.context.documentId;
  }

  get increaseShareCapitalDecision() {
    return this.context.currentDecisions.find(
      (d) => d.type === DecisionType.increaseOfShareCapital,
    );
  }

  get isIncreaseShareCapitalDecisionAdded() {
    return this.increaseShareCapitalDecision !== undefined;
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get requiredDocumentType() {
    return this.increaseShareCapitalDecision?.includeInGeneralMeeting
      ? DocumentType.general_meeting
      : DocumentType.board_meeting;
  }

  get requiredDocumentLabel(): TMessage {
    return this.increaseShareCapitalDecision?.includeInGeneralMeeting
      ? { key: "documents.subscriptionForm.sendRequiredDocument.generalMeeting" }
      : { key: "documents.subscriptionForm.sendRequiredDocument.boardMeeting" };
  }

  get requiredDocumentItemSentForSigning() {
    return this.context.currentDocuments.find((d) => d.type === this.requiredDocumentType);
  }

  get isRequiredDocumentSentForSigning() {
    return (
      this.context.documentStatuses.find(
        (d) => d.documentId === this.requiredDocumentItemSentForSigning?.documentId,
      )?.sentAt !== undefined
    );
  }

  get isDownloadDocumentDisabled() {
    return this.documentStatus?.numberOfSignedDocuments === 0;
  }

  get showSendRequiredDocument() {
    return this.isIncreaseShareCapitalDecisionAdded && !this.isRequiredDocumentSentForSigning;
  }

  get isSentForSigning() {
    return (
      this.documentStatus !== undefined &&
      [
        DocumentSigningStatus.SENT_FOR_SIGNING,
        DocumentSigningStatus.SIGNING_IN_PROGRESS,
        DocumentSigningStatus.SIGNING_COMPLETE,
      ].includes(this.documentStatus?.signingStatus)
    );
  }

  get downloadSignedSubscriptionFormsAction() {
    return downloadSignedSubscriptionFormsAction({
      document: this.documentItem,
      isSentForSigning: this.isSentForSigning,
      numberOfSignedDocuments: this.documentStatus?.numberOfSignedDocuments,
    });
  }

  openBatchPreviewAction(documentTitle: string) {
    return openSubscriptionFormBatchPreviewDialogAction({
      document: this.documentItem,
      filePreview: (file: FileToPreview) => {
        const filename = `${documentTitle} - ${file.name}.pdf`;
        return {
          filename,
          file: {
            url: `/api/document/view-subscription-form/${this.documentId}/${this.context.orgNo}?ref=${file.reference}`,
          },
        };
      },
      isSentForSigning: this.isSentForSigning,
    });
  }

  get openSendForSigningDialogAction() {
    if (this.isIncreaseShareCapitalDecisionAdded) {
      return openSendForSigningDialogAction({
        document: this.documentItem,
        signingStatus: this.documentStatus?.signingStatus,
        showUnsupportedAttachmentWarning: false,
      });
    }
  }
}

export function applyForm(form: SubscriptionFormForm): SubscriptionForm {
  return {
    type: DocumentType.subscription_form,
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
    additionalDetailsTexts: form.additionalDetailsTextModel.localizedTexts,
  };
}

export enum SubscriptionFormCustomPanel {
  settings = "settings",
}

export function getSubscriptionFormCustomPanels(form: SubscriptionFormForm): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: SubscriptionFormCustomPanel.settings,
      documentId: form.documentId,
      icon: "gear",
      heading: { key: "dbot.settings" },
      label: documentLanguageLabels[form.documentLanguage.value],
    },
  ];
}
